import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { movieService, MoviesQueryParams } from '@/services/movieService';
import { CreateMovieRequest } from '@/types/model';

export const movieKeys = {
  all: ['movies'] as const,
  lists: () => [...movieKeys.all, 'list'] as const,
  list: (params: MoviesQueryParams) => [...movieKeys.lists(), params] as const,
  details: () => [...movieKeys.all, 'detail'] as const,
  detail: (id: string) => [...movieKeys.details(), id] as const,
};

export const useMovies = (params: MoviesQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: movieKeys.list(params),
    queryFn: () => movieService.getMovies(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useMovieList = (params: MoviesQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: movieKeys.list({ ...params, _view: 'list' }),
    queryFn: () => movieService.getMovieList(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useMovie = (id: string) => {
  return useQuery({
    queryKey: movieKeys.detail(id),
    queryFn: () => movieService.getMovieById(id),
    enabled: !!id,
  });
};

export const useCreateMovie = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (movieData: CreateMovieRequest) =>
      movieService.createMovie(movieData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: movieKeys.lists() });
    },
  });
};

export const useUpdateMovie = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateMovieRequest> }) =>
      movieService.updateMovie(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: movieKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: movieKeys.lists() });
    },
  });
};

export const useDeleteMovie = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => movieService.deleteMovie(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: movieKeys.lists() });
    },
  });
};

export const useToggleMovieStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      movieService.toggleMovieStatus(id, active),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: movieKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: movieKeys.lists() });
    },
  });
};

export const usePromoteMovie = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => movieService.promoteMovie(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: movieKeys.lists() });
    },
  });
};
